import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { X } from "lucide-react";
import { inputClass } from "@/components/Shared";
import { postEnquiry } from "@/lib/api";
import { trackEvent, leadContext } from "@/lib/analytics";
import { CLINIC, RESPONSE_PROMISE } from "@/data/content";
import { getHairStage } from "@/lib/hairStage";
import { HairStageChip } from "@/components/HairStageChip";

export const ConciergeButton = ({ label = "Request a consultation", practitioner, hair = false, className = "", testId }) => {
  const concierge = useConcierge();
  return (
    <button type="button" onClick={() => concierge.open({ practitioner, hair })} data-testid={testId} className={className}>
      {label}
    </button>
  );
};


const ConciergeContext = createContext({ open: () => {} });

export const useConcierge = () => useContext(ConciergeContext);

export const PRACTITIONERS = [
  "Not sure — please advise",
  "Dr Kristy Truong",
  "Dr Anthony Chaffee",
  "Dr Kiran Shahid",
  "Lizzie Stock (skin, laser, devices)",
];

const CALL_TIMES = ["Morning", "Afternoon", "Evening"];

const Panel = ({ isOpen, preset, onClose }) => {
  const { register, handleSubmit, reset } = useForm();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);
  const hair = !!preset?.hair;

  useEffect(() => {
    if (!isOpen) return;
    returnFocusRef.current = document.activeElement;
    const first = panelRef.current?.querySelector("input, select, textarea, button");
    first?.focus();
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "Tab" && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll(
          'input, select, textarea, button, a[href]'
        );
        const list = [...focusables].filter((el) => !el.disabled && el.offsetParent !== null);
        if (!list.length) return;
        const firstEl = list[0];
        const lastEl = list[list.length - 1];
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      returnFocusRef.current?.focus?.();
    };
  }, [isOpen, onClose]);

  const onSubmit = async (data) => {
    setBusy(true);
    try {
      if (hair) {
        data.conversation = "In person at Cottesloe";
        const stage = getHairStage();
        if (stage) data.stage = stage;
      }
      const lead = leadContext("consult_request", data.practitioner);
      await postEnquiry("concierge", { ...data, meta: lead });
      trackEvent("consult_request", { page: window.location.pathname, practitioner: data.practitioner, source: "concierge-panel" }, lead);
      setDone(true);
      reset();
      toast.success(`Thank you. We'll be in touch ${RESPONSE_PROMISE}.`);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[70] bg-deep-sea/40"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.aside
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label={hair ? "Request a hair assessment" : "Request a consultation"}
            data-testid="concierge-panel"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[480px] bg-sand overflow-y-auto"
          >
            <div className="min-h-full flex flex-col p-8 sm:p-10">
              <div className="flex items-start justify-between">
                <img src="/images/lumiere-logo.png" alt="" className="h-6 w-auto" />
                <button onClick={onClose} data-testid="concierge-close" aria-label="Close"
                  className="text-deep-sea hover:text-gold transition-colors p-1 -mr-1">
                  <X className="w-5 h-5" strokeWidth={1.25} />
                </button>
              </div>

              {done ? (
                <div className="flex-1 flex flex-col justify-center" data-testid="concierge-success">
                  <h3 className="text-deep-sea">Thank you.</h3>
                  <p className="text-ink/80 mt-4 leading-[1.85]">We'll be in touch {RESPONSE_PROMISE}.</p>
                  <p className="text-soft-grey text-sm mt-8">{CLINIC.address}</p>
                  <button onClick={onClose} className="link-sea text-sm mt-10 self-start" data-testid="concierge-done-close">
                    Return to the site
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-deep-sea mt-10" data-testid="concierge-heading">
                    {hair ? "Request a hair assessment" : "Request a consultation"}
                  </h3>
                  <p className="text-ink/75 text-sm mt-4 leading-[1.85]">
                    {hair
                      ? "Tell us a little about your hair and what you're hoping for. Every hair programme here begins with an assessment — and your request is confidential."
                      : "Tell us a little about what you're hoping for, and we'll call you within one business day to arrange the right consultation with the right practitioner. Your request is confidential."}
                  </p>

                  <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" data-testid="concierge-form">
                    <div className="hidden" aria-hidden="true">
                      <input type="text" tabIndex={-1} autoComplete="off" {...register("website")} />
                    </div>
                    {hair && <HairStageChip />}
                    <div>
                      <label htmlFor="concierge-name" className="eyebrow block mb-2">Full name</label>
                      <input id="concierge-name" required data-testid="concierge-name-input" className={inputClass} {...register("name", { required: true })} />
                    </div>
                    <div>
                      <label htmlFor="concierge-email" className="eyebrow block mb-2">Email</label>
                      <input id="concierge-email" type="email" required data-testid="concierge-email-input" className={inputClass} {...register("email", { required: true })} />
                    </div>
                    <div>
                      <label htmlFor="concierge-phone" className="eyebrow block mb-2">Mobile</label>
                      <input id="concierge-phone" type="tel" required data-testid="concierge-phone-input" className={inputClass} {...register("phone", { required: true })} />
                    </div>
                    <div>
                      <label htmlFor="concierge-practitioner" className="eyebrow block mb-2">Preferred practitioner</label>
                      <select id="concierge-practitioner" defaultValue={preset?.practitioner || PRACTITIONERS[0]} key={preset?.practitioner || "default"}
                        data-testid="concierge-practitioner-input" className={inputClass} {...register("practitioner")}>
                        {PRACTITIONERS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                    </div>
                    {hair && (
                      <div>
                        <label htmlFor="concierge-location" className="eyebrow block mb-2">Where are you based? <span className="normal-case tracking-normal text-soft-grey">(optional)</span></label>
                        <input id="concierge-location" type="text" data-testid="concierge-location-input" placeholder="Cottesloe, regional WA, FIFO…" className={inputClass} {...register("location")} />
                      </div>
                    )}
                    <div>
                      <label htmlFor="concierge-message" className="eyebrow block mb-2">{hair ? "What's happening with your hair?" : "What are you hoping for?"}</label>
                      <textarea id="concierge-message" rows={3} maxLength={500} data-testid="concierge-message-input"
                        className={inputClass} {...register("message")} />
                    </div>
                    <div>
                      <span className="eyebrow block mb-2">Best time to call</span>
                      <div className="flex gap-2">
                        {CALL_TIMES.map((t) => (
                          <label key={t} className="flex-1">
                            <input type="radio" value={t} className="peer sr-only" {...register("best_time")} />
                            <span className="block text-center border border-deep-sea/25 py-2.5 text-xs tracking-[0.1em] uppercase cursor-pointer transition-colors duration-300 peer-checked:bg-deep-sea peer-checked:text-warm-white peer-checked:border-deep-sea peer-focus-visible:outline peer-focus-visible:outline-1 peer-focus-visible:outline-gold"
                              data-testid={`concierge-time-${t.toLowerCase()}`}>
                              {t}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer pt-1">
                      <input type="checkbox" required data-testid="concierge-consent-checkbox"
                        className="mt-0.5 w-4 h-4 accent-[#233A35]" {...register("consent", { required: true })} />
                      <span className="text-xs text-ink/70 leading-relaxed">
                        I consent to Lumière Aesthetics collecting these details so the practice can respond to my
                        request, as described in the <Link to="/privacy" className="link-sea" onClick={onClose}>privacy policy</Link>.
                      </span>
                    </label>
                    <button type="submit" disabled={busy} data-testid="concierge-submit-button"
                      className="btn-gold w-full px-8 disabled:opacity-60">
                      {busy ? "Sending…" : hair ? "Request a hair assessment" : "Request a consultation"}
                    </button>
                  </form>

                  <p className="mt-8 text-center">
                    <Link to="/book" onClick={onClose} className="link-sea text-sm" data-testid="concierge-book-online">
                      Book online
                    </Link>
                  </p>
                </>
              )}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export const ConciergeProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [preset, setPreset] = useState(null);
  const open = useCallback((config) => {
    const next = typeof config === "string" ? { practitioner: config } : config || null;
    setPreset(next);
    setIsOpen(true);
  }, []);
  const close = useCallback(() => setIsOpen(false), []);
  return (
    <ConciergeContext.Provider value={{ open }}>
      {children}
      <Panel isOpen={isOpen} preset={preset} onClose={close} />
    </ConciergeContext.Provider>
  );
};
